import * as THREE from 'three';
import { Generator } from './Generator.js';
import { buildInstancer } from '../td/instancer.js';
import { SourceSampler } from '../td/source-sampler.js';

const GLYPHS = {
  'A': 'A.png',
  'I': 'I.png',
  ' ': '@.png',
  'D': 'D.png',
  'Y': 'Y.png',
  '.': 'dot.png',
};

// Ordered from least → most "ink". Luminance buckets map to indices here.
// 1.0 (white, brightest) → space; 0.0 (black, darkest) → densest glyph.
// We'll invert: bright source → empty/light glyph; dark source → heavy glyph.
const RAMP = [' ', '.', 'I', 'Y', 'A', 'D'];

// Vite picks up source images from assets/td/sources/*
const sourceUrls = import.meta.glob('../../assets/td/sources/*.{png,jpg,jpeg,webp}', {
  eager: true,
  query: '?url',
  import: 'default',
});

export class AIDayLetters extends Generator {
  constructor() {
    super({
      name: 'AI DAY Letters',
      category: 'intense',
      schema: {
        cols:        { default: 80,  min: 10, max: 200, step: 1, label: 'Cols' },
        rows:        { default: 50,  min: 10, max: 200, step: 1, label: 'Rows' },
        emissive:    { default: 1.0, min: 0.0, max: 4.0, step: 0.05, label: 'Emissive' },
        tint:        { default: '#6effd6', type: 'color', label: 'Tint' },
        monochrome:  { default: 1.0, min: 0.0, max: 1.0, step: 0.01, label: 'Monochrome' },
        contrast:    { default: 1.4, min: 0.5, max: 3.0, step: 0.05, label: 'Contrast' },
        brightness:  { default: 0.0, min: -0.5, max: 0.5, step: 0.01, label: 'Brightness' },
        invert:      { default: false, type: 'bool', label: 'Invert ramp' },
        animSpeed:   { default: 0.0, min: 0.0, max: 4.0, step: 0.05, label: 'Anim speed' },
      },
    });
    this.bundle = null;
    this.sampler = new SourceSampler();
    this._sourceReady = false;
    this._gridDirty = true;
    this._lastCols = 0;
    this._lastRows = 0;
  }

  async onEnter(scene, engine) {
    this.engine = engine;
    this._scene = scene;

    // Pick first available source image; fall back to a generated gradient.
    const firstUrl = Object.values(sourceUrls)[0];
    if (firstUrl) {
      try { await this.sampler.loadImage(firstUrl); }
      catch (err) { console.warn('AIDayLetters: source load failed', err); this._fallbackSource(); }
    } else {
      this._fallbackSource();
    }
    this._sourceReady = true;

    await this._buildGrid();
  }

  _fallbackSource() {
    // Procedural radial gradient, so something is visible without a source file.
    const c = document.createElement('canvas');
    c.width = 512; c.height = 512;
    const ctx = c.getContext('2d');
    const g = ctx.createRadialGradient(256, 256, 30, 256, 256, 256);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(1, '#000000');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 512, 512);
    this.sampler.setSource(c, 512, 512);
  }

  async _buildGrid() {
    if (this.bundle) {
      this._scene.remove(this.bundle.mesh);
      this.bundle.mesh.geometry.dispose();
      this.bundle.material.dispose();
      this.bundle.atlasTexture.dispose();
      this.bundle = null;
    }

    const cols = Math.max(1, Math.floor(this.v('cols')));
    const rows = Math.max(1, Math.floor(this.v('rows')));
    const count = cols * rows;

    this.bundle = await buildInstancer({
      count,
      glyphChars: RAMP,
      glyphMap: GLYPHS,
      atlas: { cellSize: 256 },
      material: {
        tint: new THREE.Color(this.v('tint')).getHex(),
        emissiveBoost: this.v('emissive'),
        monochrome: this.v('monochrome'),
        additive: true,
      },
      quadSize: 1.0,
    });

    this._scene.add(this.bundle.mesh);
    this.track(this.bundle.mesh);
    this._lastCols = cols;
    this._lastRows = rows;
    this._writeGrid();
  }

  // Compute layout + sample source per cell.
  _writeGrid() {
    if (!this.bundle || !this._sourceReady || !this.engine) return;

    const cols = this._lastCols;
    const rows = this._lastRows;
    const aspect = this.engine.width / this.engine.height;

    // Fit into [-aspect, aspect] x [-1, 1]
    const cellW = (2 * aspect) / cols;
    const cellH = 2 / rows;
    const cellSize = Math.min(cellW, cellH);

    const totalW = cellSize * cols;
    const totalH = cellSize * rows;
    const x0 = -totalW / 2 + cellSize / 2;
    const y0 =  totalH / 2 - cellSize / 2;

    const contrast = this.v('contrast');
    const brightness = this.v('brightness');
    const invert = this.v('invert');

    // Source aspect-fit into the grid sample area
    const srcAspect = this.sampler.aspect;
    const gridAspect = cols / rows;
    let su0 = 0, sv0 = 0, su1 = 1, sv1 = 1;
    if (srcAspect > gridAspect) {
      const trim = (1 - gridAspect / srcAspect) / 2;
      su0 = trim; su1 = 1 - trim;
    } else {
      const trim = (1 - srcAspect / gridAspect) / 2;
      sv0 = trim; sv1 = 1 - trim;
    }

    let i = 0;
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const u = su0 + ((x + 0.5) / cols) * (su1 - su0);
        const v = sv0 + ((y + 0.5) / rows) * (sv1 - sv0);
        const s = this.sampler.sample(u, v);

        let lum = (s.lum - 0.5) * contrast + 0.5 + brightness;
        lum = Math.max(0, Math.min(1, lum));
        const ramp = invert ? lum : 1 - lum;
        const idx = Math.min(RAMP.length - 1, Math.max(0, Math.floor(ramp * RAMP.length)));
        const ch = RAMP[idx];

        this.bundle.setGlyph(i, ch);
        this.bundle.setColor(i, s.r, s.g, s.b);
        this.bundle.setOffset(i, x0 + x * cellSize, y0 - y * cellSize, 0);
        this.bundle.setScale(i, cellSize);
        i++;
      }
    }
    this.bundle.flushAll();
  }

  onResize() {
    if (this.bundle) this._writeGrid();
  }

  onParamChange(key, value) {
    if (!this.bundle) return;
    if (key === 'emissive') {
      this.bundle.material.uniforms.uEmissive.value = value;
    } else if (key === 'tint') {
      this.bundle.material.uniforms.uTint.value.set(value);
    } else if (key === 'monochrome') {
      this.bundle.material.uniforms.uMonochrome.value = value;
    } else if (key === 'cols' || key === 'rows') {
      this._gridDirty = true;
    } else if (key === 'contrast' || key === 'brightness' || key === 'invert') {
      this._writeGrid();
    }
  }

  update(dt, time, engine) {
    if (this._gridDirty) {
      this._gridDirty = false;
      this._buildGrid();
    }
    if (this.v('animSpeed') > 0 && this.bundle) {
      // re-quantize ramp with a time wobble for animated stippling
      const aRot = this.bundle.attributes.aRotation;
      const speed = this.v('animSpeed');
      for (let i = 0; i < aRot.count; i++) {
        aRot.array[i * 3 + 2] = Math.sin(time * speed + i * 0.13) * 0.05;
      }
      aRot.needsUpdate = true;
    }
  }
}
